import React from "react";
import type { PopupQuestion } from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import { textCss } from "./engine";

interface QuestionOptionsProps {
  question: PopupQuestion;
  step: ResolvedStepDesign;
  /** Tap on an answer: (question.key, stored value). */
  onAnswer: (key: string, value: string) => void;
  /** Decline link tapped. */
  onDismiss?: () => void;
  /** Static preview / builder: renders, never answers. */
  inert?: boolean;
}

/** The tappable answers of a question page. Styled from the question's own
 *  option_style with the design's button element as the fallback, so a page
 *  matches the CTA unless told otherwise. Styled inline (no utility classes)
 *  so host stylesheets cannot restyle the buttons. */
export const QuestionOptions: React.FC<QuestionOptionsProps> = ({
  question,
  step,
  onAnswer,
  onDismiss,
  inert = false,
}) => {
  const btn = step.elements.button;
  const st = question.option_style ?? {};
  const options = (question.options || []).filter((o) => o && String(o.label || "").trim());
  const grid = question.layout === "grid";
  const shadow =
    st.shadow_offset && st.shadow_offset > 0
      ? `${st.shadow_offset}px ${st.shadow_offset}px 0 ${st.shadow_color || "#fde047"}`
      : "none";
  const fontSize = st.font_size_px && st.font_size_px > 0 ? st.font_size_px : btn.fontSizePx > 0 ? btn.fontSizePx : 15;
  const mobileFs = st.mobile_font_size_px && st.mobile_font_size_px > 0 ? st.mobile_font_size_px : 0;

  const prompt = String(question.prompt || "").trim();
  // body is always present on a normalized design; the fallbacks only guard bare test fixtures
  const bodyEl = step.elements.body || step.elements.subheading || step.elements.heading;
  const bodyMobile = bodyEl.mobileSizePx && bodyEl.mobileSizePx > 0 ? bodyEl.mobileSizePx : 0;
  return (
    <div
      className="flowapt-question"
      style={{
        display: "grid",
        gridTemplateColumns: grid ? "1fr 1fr" : "1fr",
        gap: grid ? 10 : 10,
        width: "100%",
      }}
    >
      {prompt && (
        <p
          className={bodyMobile ? "fp-fs-m" : undefined}
          style={{
            ...textCss("body", bodyEl),
            margin: "0 0 2px",
            ...(bodyMobile ? ({ "--fp-fs-m": `${bodyMobile}px` } as React.CSSProperties) : {}),
          }}
        >
          {prompt}
        </p>
      )}
      {options.map((o, i) => (
        <button
          key={i}
          type="button"
          className={mobileFs ? "fp-fs-m" : undefined}
          disabled={inert}
          onClick={() => {
            if (!inert) onAnswer(question.key, (o.value ?? o.label).trim());
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            minHeight: st.height_px && st.height_px > 0 ? st.height_px : 48,
            padding: "0 16px",
            boxSizing: "border-box",
            background: st.background ?? btn.background,
            color: st.text_color ?? btn.textColor,
            border: `${st.border_width ?? 0}px solid ${st.border_color ?? st.background ?? btn.background}`,
            borderRadius: st.radius ?? btn.radius,
            boxShadow: shadow,
            fontFamily: "inherit",
            fontWeight: st.font_weight ?? 600,
            fontSize,
            ...(mobileFs ? { ["--fp-fs-m" as string]: `${mobileFs}px` } : {}),
            textTransform: st.uppercase ? "uppercase" : "none",
            letterSpacing: st.uppercase ? "0.06em" : undefined,
            lineHeight: 1.2,
            cursor: inert ? "default" : "pointer",
            textAlign: "center",
          }}
        >
          {o.label}
        </button>
      ))}
      {question.dismiss_text && (
        <button
          type="button"
          onClick={() => {
            if (!inert) onDismiss?.();
          }}
          style={{
            gridColumn: grid ? "1 / -1" : undefined,
            marginTop: 4,
            background: "transparent",
            border: "none",
            padding: 4,
            color: step.elements.subheading.color || step.elements.heading.color,
            opacity: 0.8,
            textDecoration: "underline",
            fontSize: 13,
            fontFamily: "inherit",
            cursor: inert ? "default" : "pointer",
          }}
        >
          {question.dismiss_text}
        </button>
      )}
    </div>
  );
};

export default QuestionOptions;
